<template>
  <div class="coupon-list">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>卡券列表</h2>
          <el-button type="primary" @click="handleAddCoupon">新增卡券</el-button>
        </div>
      </template>
      
      <!-- 搜索和筛选 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="关键词">
          <el-input v-model="searchForm.keyword" placeholder="请输入标题或描述" clearable @keyup.enter="searchCoupons" />
        </el-form-item>
        <el-form-item label="分类">
          <el-select v-model="searchForm.category" placeholder="请选择券分类" clearable style="width: 200px;">
            <el-option label="鉴别服务券" value="authentication" />
            <el-option label="保养券" value="maintenance" />
            <el-option label="新品预定权益" value="pre_order" />
            <el-option label="活动邀约权益" value="activity_invitation" />
            <el-option label="上门回收券" value="home_recycle" />
            <el-option label="维修券" value="repair" />
            <el-option label="周边兑换券" value="peripheral_exchange" />
            <el-option label="赠品兑换券" value="gift_exchange" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择券状态" clearable style="width: 150px;">
            <el-option label="启用" value="active" />
            <el-option label="禁用" value="inactive" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="searchCoupons">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
      
      <!-- 卡券列表 -->
      <el-table :data="coupons" v-loading="loading" style="width: 100%">
  
        <el-table-column label="标题" min-width="200">
          <template #default="scope">
            <img
              v-if="scope.row.cover"
              :src="getCoverImageUrl(scope.row.cover)"
              fit="cover"
              style="width: 80px; height: 60px; border-radius: 4px"
              :preview-src-list="[getCoverImageUrl(scope.row.cover)]"
            />
            <div class="coupon-title-container">
              <div class="coupon-main-title">{{ scope.row.title }}</div>
              <div class="coupon-sub-title">{{ scope.row.subtitle || '' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="category" label="分类" width="150">
          <template #default="scope">
            <div class="category-with-badge">
              <el-tag>{{ this.getCategoryName(scope.row.category) }}</el-tag>
              <el-tag size="small" :type="scope.row.type === 'virtual' ? 'success' : 'info'" class="type-badge">
                {{ scope.row.type === 'virtual' ? '虚拟' : '实物' }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="valid_days" label="有效期(天)" width="120" />
        <el-table-column prop="total_quantity" label="总库存" width="100">
          <template #default="scope">
            <span>{{ scope.row.total_quantity === 0 ? '无限制' : scope.row.total_quantity }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="available_quantity" label="可用库存" width="100" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag type="success" v-if="scope.row.status === 'active'">启用</el-tag>
            <el-tag type="warning" v-else-if="scope.row.status === 'inactive'">禁用</el-tag>
            <el-tag type="danger" v-else>已过期</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleEditCoupon(scope.row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDeleteCoupon(scope.row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 新增/编辑卡券抽屉 -->
    <el-drawer
      v-model="drawerVisible"
      title="卡券信息"
      size="80%"
      direction="rtl"
      @close="handleDrawerClose"
    >
      <div class="coupon-form-container">
        <el-form :model="couponForm" label-width="120px" size="default" class="coupon-form">
          <!-- 基本信息 -->
          <el-divider content-position="left">基本信息</el-divider>
          
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="卡券标题" prop="title" required>
                <el-input v-model="couponForm.title" placeholder="请输入卡券标题" maxlength="255" show-word-limit />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="卡券副标题" prop="subtitle">
                <el-input v-model="couponForm.subtitle" placeholder="请输入卡券副标题" maxlength="255" show-word-limit />
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-row>
            <el-col :span="24">
              <el-form-item label="卡券封面" prop="cover">
                <el-upload
                  v-model:file-list="fileList"
                :action="`${apiBaseUrl}/manage/upload/image`"
                  :auto-upload="true"
                  list-type="picture-card"
                  :on-preview="handlePictureCardPreview"
                  :on-remove="handleRemove"
                  :before-upload="beforeUpload"
                  :on-success="handleImageUploadSuccess"
                  :on-error="handleImageUploadError"
                  class="cover-uploader"
                >
                  <el-icon><Plus /></el-icon>
                  <template #tip>
                    <div class="el-upload__tip">
                      建议尺寸：800x600px，支持 JPG/PNG 格式，大小不超过 2MB
                    </div>
                  </template>
                </el-upload>
                <el-dialog v-model="dialogVisible" width="80%">
                  <img w-full :src="dialogImageUrl" alt="Preview Image" style="width: 100%; height: auto;" />
                </el-dialog>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-row>
            <el-col :span="24">
              <el-form-item label="卡券描述" prop="description" required>
                <div class="editor-container">
                  <!-- 使用富文本编辑器组件 -->
                  <RichTextEditor v-model="couponForm.description" />
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          
          <!-- 类型与有效期 -->
          <el-divider content-position="left">类型与有效期</el-divider>
          
          <el-row>
            <el-col :span="24">
              <div class="type-validity-section">
                <!-- 卡券类型设置 -->
                <div class="section-subtitle">卡券类型</div>
                <el-row :gutter="24" class="section-content">
                  <el-col :span="12">
                    <el-form-item label="券分类" prop="category" required>
                      <el-select v-model="couponForm.category" placeholder="请选择券分类" style="width: 100%;">
                        <el-option-group label="------ 虚拟券 ------">
                          <template #prefix>
                            <span class="group-prefix">📱</span>
                          </template>
                          <el-option label="鉴别服务券" value="authentication" />
                          <el-option label="保养券" value="maintenance" />
                          <el-option label="新品预定权益" value="pre_order" />
                          <el-option label="活动邀约权益" value="activity_invitation" />
                          <el-option label="上门回收券" value="home_recycle" />
                          <el-option label="维修券" value="repair" />
                        </el-option-group>
                        <el-divider style="margin: 12px 0; background-color: #e0e0e0; height: 2px;" />
                        <el-option-group label="------ 实物券 ------">
                          <template #prefix>
                            <span class="group-prefix">📦</span>
                          </template>
                          <el-option label="周边兑换券" value="peripheral_exchange" />
                          <el-option label="赠品兑换券" value="gift_exchange" />
                        </el-option-group>
                      </el-select>
                      <div class="form-hint">选择卡券的类型和用途</div>
                    </el-form-item>
                  </el-col>
                  
                  <!-- 实物商品选择（条件显示，仅实物券需要） -->
                  <el-collapse-transition>
                    <el-col :span="12" v-if="(couponForm.category === 'peripheral_exchange' || couponForm.category === 'gift_exchange')">
                      <el-form-item 
                        label="关联商品" 
                        prop="product_id" 
                        required
                      >
                        <el-select v-model="couponForm.product_id" placeholder="请选择实物商品" style="width: 100%;">
                          <el-option
                            v-for="product in products"
                            :key="product.id"
                            :label="product.name"
                            :value="product.id"
                          >
                            <span>{{ product.name }}</span>
                          </el-option>
                        </el-select>
                        <div class="form-hint">选择实物券对应的商品，用户领取后可兑换</div>
                      </el-form-item>
                    </el-col>
                  </el-collapse-transition>
                </el-row>
                
                <!-- 有效期与状态设置 -->
                <div class="section-subtitle">有效期与状态</div>
                <el-row :gutter="24" class="section-content">
                  <el-col :span="8">
                    <el-form-item label="领取后有效期" prop="valid_days" required>
                      <el-input-number
                        v-model="couponForm.valid_days"
                        :min="1"
                        :max="365"
                        placeholder="请输入天数"
                        style="width: 100%;"
                      >
                        <template #append>天</template>
                      </el-input-number>
                      <div class="form-hint">用户领取后，卡券的有效天数</div>
                    </el-form-item>
                  </el-col>
                  <el-col :span="8">
                    <el-form-item label="卡券状态" prop="status" required>
                      <el-select v-model="couponForm.status" placeholder="请选择状态" style="width: 100%;">
                        <el-option label="启用" value="active" />
                        <el-option label="禁用" value="inactive" />
                      </el-select>
                      <div class="form-hint">启用后，卡券可正常发放和使用</div>
                    </el-form-item>
                  </el-col>
                </el-row>
              </div>
            </el-col>
          </el-row>
          
          <!-- 库存设置 -->
          <el-divider content-position="left">库存设置</el-divider>
          
          <el-row>
            <el-col :span="24">
              <div class="inventory-section">
                <!-- 无库存限制选项 -->
                <el-form-item>
                  <el-checkbox v-model="couponForm.unlimited" @change="handleUnlimitedChange" class="unlimited-checkbox">
                    <span class="checkbox-label">无库存限制</span>
                    <span class="checkbox-desc">勾选后，卡券将不受库存数量限制</span>
                  </el-checkbox>
                </el-form-item>
                
                <!-- 库存数量设置（仅当有限制时显示） -->
                <el-collapse-transition>
                  <div v-if="!couponForm.unlimited" class="inventory-quantity-section">
                    <el-row :gutter="24">
                      <el-col :span="12">
                        <el-form-item label="总库存" prop="total_quantity" required>
                          <el-input-number
                            v-model="couponForm.total_quantity"
                            :min="1"
                            placeholder="请输入总库存数量"
                            style="width: 100%;"
                            :disabled="couponForm.unlimited"
                          >
                            <template #prepend>总量</template>
                          </el-input-number>
                          <div class="form-hint">设置卡券的最大发放数量</div>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="当前可用库存" prop="available_quantity">
                          <el-input-number
                            v-model="couponForm.available_quantity"
                            :min="0"
                            placeholder="自动计算"
                            disabled
                            style="width: 100%;"
                          >
                            <template #prepend>可用</template>
                          </el-input-number>
                          <div class="form-hint">系统根据已发放数量自动计算</div>
                        </el-form-item>
                      </el-col>
                    </el-row>
                  </div>
                </el-collapse-transition>
              </div>
            </el-col>
          </el-row>
          
          <!-- 表单操作 -->
          <el-row>
            <el-col :span="24" class="form-actions">
              <el-button type="primary" @click="submitForm" :loading="submitting" size="large" :icon="CircleCheckFilled">
                {{ isEditing ? '更新卡券' : '创建卡券' }}
              </el-button>
              <el-button @click="resetForm" size="large" :icon="RefreshRight">
                重置
              </el-button>
              <el-button @click="drawerVisible = false" size="large" :icon="CircleCloseFilled">
                取消
              </el-button>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useRouter } from 'vue-router';
import { Plus, CircleCheckFilled, RefreshRight, CircleCloseFilled } from '@element-plus/icons-vue';
import RichTextEditor from '../../components/RichTextEditor.vue';
import couponApi from '../../api/coupon.js';
import request from '../../api/axios';

export default {
  name: 'CouponList',
  components: {
    RichTextEditor,
    Plus,
    CircleCheckFilled,
    RefreshRight,
    CircleCloseFilled,
  },
  setup() {
    const router = useRouter();
    
    // 卡券数据
    const coupons = ref([]);
    // 加载状态
    const loading = ref(false);
    // 查询条件
    const searchForm = reactive({
      keyword: '',
      type: '',
      category: '',
      status: '',
    });
    // 分页信息
    const pagination = reactive({
      page: 1,
      pageSize: 10,
      total: 0,
    });

    // 券分类名称映射
    const categoryNames = {
      authentication: '鉴别服务券',
      maintenance: '保养券',
      pre_order: '新品预定权益',
      activity_invitation: '活动邀约权益',
      home_recycle: '上门回收券',
      repair: '维修券',
      peripheral_exchange: '周边兑换券',
      gift_exchange: '赠品兑换券',
    };

    // 抽屉相关
    const drawerVisible = ref(false);
    const submitting = ref(false);
    const isEditing = ref(false);
    const editingId = ref(null);
    
    // 商品列表
    const products = ref([]);
    
    // 表单数据
    const couponForm = reactive({
      title: '',
      subtitle: '',
      cover: '',
      description: '',
      category: 'authentication',
      valid_days: 30,
      total_quantity: 0,
      available_quantity: 0,
      status: 'active',
      unlimited: true,
      product_id: null,
    });
    
    // 获取API基础URL，与axios配置保持一致
    const apiBaseUrl = ref(process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api');
    
    // 获取token，处理localStorage不可用的情况
    const getToken = () => {
      try {
        return localStorage.getItem('token') || '';
      } catch (e) {
        console.error('Failed to access localStorage:', e);
        return '';
      }
    };
    
    // 认证token
    const authToken = ref(getToken());
    
    // 上传相关
    const fileList = ref([]);
    const dialogImageUrl = ref('');
    const dialogVisible = ref(false);

    // 获取分类中文名称
    const getCategoryName = (category) => {
      return categoryNames[category] || category;
    };

    // 加载卡券列表
    const loadCoupons = () => {
      loading.value = true;
      
      // 构建请求参数
      const params = {
        keyword: searchForm.keyword,
        category: searchForm.category,
        status: searchForm.status,
        page: pagination.page,
        per_page: pagination.pageSize,
      };
      
      // 调用API获取卡券列表
      couponApi.getCoupons(params)
        .then(response => {
          coupons.value = response.data || [];
          pagination.total = response.total || 0;
        })
        .catch(error => {
          ElMessage.error('获取卡券列表失败：' + (error.message || '网络错误'));
          console.error('获取卡券列表失败：', error);
        })
        .finally(() => {
          loading.value = false;
        });
    };
    
    // 加载商品列表
    const loadProducts = () => {
      couponApi.getCouponCreateData()
        .then(response => {
          products.value = response.data.products || [];
        })
        .catch(error => {
          ElMessage.error('获取商品列表失败：' + (error.message || '网络错误'));
          console.error('获取商品列表失败：', error);
        });
    };

    // 搜索卡券
    const searchCoupons = () => {
      pagination.page = 1;
      loadCoupons();
    };

    // 重置搜索条件
    const resetSearch = () => {
      searchForm.keyword = '';
      searchForm.type = '';
      searchForm.category = '';
      searchForm.status = '';
      searchCoupons();
    };

    // 切换页码
    const handleCurrentChange = (page) => {
      pagination.page = page;
      loadCoupons();
    };

    // 切换每页条数
    const handleSizeChange = (pageSize) => {
      pagination.pageSize = pageSize;
      pagination.page = 1;
      loadCoupons();
    };

    // 新增卡券
    const handleAddCoupon = () => {
      // 重置表单
      resetForm();
      // 加载商品列表
      loadProducts();
      // 设置为新增模式
      isEditing.value = false;
      editingId.value = null;
      // 打开抽屉
      drawerVisible.value = true;
    };

    // 编辑卡券
    const handleEditCoupon = (row) => {
      // 设置为编辑模式
      isEditing.value = true;
      editingId.value = row.id;
      // 加载商品列表
      loadProducts();
      // 填充表单数据
      Object.assign(couponForm, {
        title: row.title,
        subtitle: row.subtitle,
        cover: row.cover,
        description: row.description,
        category: row.category,
        valid_days: row.valid_days,
        total_quantity: row.total_quantity,
        available_quantity: row.available_quantity,
        status: row.status,
        unlimited: row.total_quantity === 0,
        product_id: row.product_id || null,
      });
      // 设置封面图片
      if (row.cover) {
        let previewUrl = row.cover;
        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
        const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
        const apiDomain = domainMatch ? domainMatch[1] : window.location.origin;
        previewUrl = `${apiDomain}${previewUrl}`;
        fileList.value = [{ 
          name: 'cover', 
          url: previewUrl,
          uid: Date.now()
        }];
        couponForm.cover = row.cover;
      } else {
        fileList.value = [];
        couponForm.cover = '';
      }
      // 打开抽屉
      drawerVisible.value = true;
    };

    // 删除卡券
    const handleDeleteCoupon = (id) => {
      ElMessageBox.confirm('确定要删除这个卡券吗？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
      .then(() => {
        // 调用删除API
        couponApi.deleteCoupon(id)
          .then(() => {
            ElMessage.success('删除卡券成功');
            // 重新加载数据
            loadCoupons();
          })
          .catch(error => {
            ElMessage.error('删除卡券失败：' + (error.message || '网络错误'));
            console.error('删除卡券失败：', error);
          });
      })
      .catch(() => {
        // 取消删除
      });
    };

    // 关闭抽屉
    const handleDrawerClose = () => {
      // 重置表单
      resetForm();
      // 清空文件列表
      fileList.value = [];
      // 关闭抽屉
      drawerVisible.value = false;
    };

    // 图片预览
    const handlePictureCardPreview = (file) => {
      dialogImageUrl.value = file.url;
      dialogVisible.value = true;
    };

    // 图片删除
    const handleRemove = (file) => {
      couponForm.cover = '';
    };

    // 图片上传前处理
    const beforeUpload = (rawFile) => {
      const isJPG = rawFile.type === 'image/jpeg' || rawFile.type === 'image/png';
      if (!isJPG) {
        ElMessage.error('请上传 JPG/PNG 格式的图片');
        return false;
      }
      const isLt2M = rawFile.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        ElMessage.error('图片大小不能超过 2MB');
        return false;
      }
      return true;
    };

    // 图片上传成功回调
    const handleImageUploadSuccess = (response, file, fileList) => {
      if (response.errno === 0) {
        let imgUrl = response.data[0].url;
        couponForm.cover = imgUrl;
        ElMessage.success('图片上传成功');
      } else {
        ElMessage.error('图片上传失败: ' + (response.message || '未知错误'));
      }
    };

    // 图片上传失败回调
    const handleImageUploadError = (error, file, fileList) => {
      let errorMessage = '网络错误';
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.response && error.response.data) {
          errorMessage = error.response.data.message || '未知错误';
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.statusText) {
          errorMessage = error.statusText;
        }
      }
      ElMessage.error('图片上传失败: ' + errorMessage);
    };

    // 处理无库存限制
    const handleUnlimitedChange = (value) => {
      if (value) {
        // 开启无库存限制
        couponForm.total_quantity = 0;
        couponForm.available_quantity = 0;
      } else {
        // 关闭无库存限制，设置默认值
        if (couponForm.total_quantity === 0) {
          couponForm.total_quantity = 100;
          couponForm.available_quantity = 100;
        }
      }
    };

    // 重置表单
    const resetForm = () => {
      Object.assign(couponForm, {
        title: '',
        subtitle: '',
        cover: '',
        description: '',
        category: 'authentication',
        valid_days: 30,
        total_quantity: 0,
        available_quantity: 0,
        status: 'active',
        unlimited: true,
        product_id: null,
      });
      fileList.value = [];
    };

    // 获取完整的图片URL
    const getCoverImageUrl = (imageUrl) => {
      if (!imageUrl) return '';
      // 如果已经是完整URL，直接返回
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      // 从API基础URL中提取域名
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
      const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
      const apiDomain = domainMatch ? domainMatch[1] : window.location.origin;
      // 拼接API域名
      return `${apiDomain}${imageUrl}`;
    };

    // 提交表单
    const submitForm = () => {
      // 验证表单
      if (!couponForm.title) {
        ElMessage.error('请输入卡券标题');
        return;
      }
      if (!couponForm.description) {
        ElMessage.error('请输入卡券描述');
        return;
      }
      
      // 根据分类自动设置类型：周边兑换券和赠品兑换券是实物券，其余都是虚拟券
      const type = couponForm.category === 'peripheral_exchange' || couponForm.category === 'gift_exchange' ? 'physical' : 'virtual';
      
      // 实物券必须选择商品
      if (type === 'physical' && !couponForm.product_id) {
        ElMessage.error('实物券必须选择关联商品');
        return;
      }
      
      submitting.value = true;
      
      // 准备提交数据
      const formData = {
        title: couponForm.title,
        subtitle: couponForm.subtitle,
        cover: couponForm.cover,
        description: couponForm.description,
        type: type,
        category: couponForm.category,
        valid_days: couponForm.valid_days,
        total_quantity: couponForm.total_quantity,
        status: couponForm.status,
      };
      
      // 只有实物券且选择了商品时才传递product_id
      if (type === 'physical' && couponForm.product_id) {
        formData.product_id = couponForm.product_id;
      }
      
      // 调用API
      const apiPromise = isEditing.value 
        ? couponApi.updateCoupon(editingId.value, formData)
        : couponApi.createCoupon(formData);
      
      apiPromise
        .then(() => {
          ElMessage.success(isEditing.value ? '更新卡券成功' : '新增卡券成功');
          // 关闭抽屉
          handleDrawerClose();
          // 重新加载数据
          loadCoupons();
        })
        .catch(error => {
          ElMessage.error((isEditing.value ? '更新卡券' : '新增卡券') + '失败：' + (error.message || '网络错误'));
          console.error((isEditing.value ? '更新卡券' : '新增卡券') + '失败：', error);
        })
        .finally(() => {
          submitting.value = false;
        });
    };

    // 根据分类自动设置类型
    watch(() => couponForm.category, (newCategory) => {
      // 除了周边兑换券和赠品兑换券，其余都是虚拟券
      if (newCategory === 'peripheral_exchange' || newCategory === 'gift_exchange') {
        // 实物券
      } else {
        // 虚拟券
      }
    });

    // 初始化数据
    onMounted(() => {
      loadCoupons();
    });

    return {
      coupons,
      loading,
      searchForm,
      pagination,
      products,
      apiBaseUrl,
      authToken,
      getCategoryName,
      getCoverImageUrl,
      loadCoupons,
      searchCoupons,
      resetSearch,
      handleCurrentChange,
      handleSizeChange,
      handleAddCoupon,
      handleEditCoupon,
      handleDeleteCoupon,
      // 抽屉相关
      drawerVisible,
      submitting,
      couponForm,
      fileList,
      dialogImageUrl,
      dialogVisible,
      handleDrawerClose,
      handlePictureCardPreview,
      handleRemove,
      beforeUpload,
      handleImageUploadSuccess,
      handleImageUploadError,
      handleUnlimitedChange,
      resetForm,
      submitForm,
    };
  },
};
</script>

<style scoped>
.coupon-list {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 表单容器样式优化 */
.coupon-form-container {
  padding: 24px;
  background-color: #fafafa;
  min-height: 100%;
}

/* 表单样式 */
.coupon-form {
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

/* 封面上传样式 */
.cover-uploader .el-upload {
  margin-bottom: 16px;
}

/* 富文本编辑器容器 */
.editor-container {
  width: 100%;
  min-height: 300px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: white;
  overflow: hidden;
}

/* 表单操作按钮样式 */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  justify-content: flex-start;
}

/* 表单操作按钮间距 */
.form-actions .el-button {
  padding: 12px 24px;
  font-size: 14px;
}

/* 表单行间距优化 */
.el-form-item {
  margin-bottom: 20px;
}

/* 分隔线样式 */
.el-divider {
  margin: 24px 0;
  font-weight: 600;
}

/* 表格行悬停效果 */
.el-table__row:hover {
  background-color: #f5f7fa !important;
}

/* 表格操作按钮样式 */
.el-table .el-button {
  margin-right: 8px;
}

/* 卡券标题容器样式 */
.coupon-title-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 主标题样式 */
.coupon-main-title {
  font-weight: bold;
  color: #333;
}

/* 副标题样式 */
.coupon-sub-title {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 库存设置样式 */
.inventory-section {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

/* 无库存限制复选框样式 */
.unlimited-checkbox {
  margin-bottom: 12px;
  display: flex;
}

/* 分类带右上角标的样式 */
.category-with-badge {
  position: relative;
  display: inline-block;
  padding: 0 4px;
}

.type-badge {
  position: absolute;
  top: -2px;
  right: -8px;
  font-size: 10px;
  height: 14px;
  line-height: 14px;
  padding: 0 5px;
  border-radius: 7px;
  transform: scale(0.85);
  white-space: nowrap;
  z-index: 1;
}

.checkbox-label {
  font-size: 14px;
  font-weight: 500;
  margin-right: 8px;
  display: block;
  margin-bottom: 4px;
}

.checkbox-desc {
  font-size: 12px;
  color: #909399;
  display: block;
  margin-left: 20px;
}

/* 库存数量设置区域 */
.inventory-quantity-section {
  margin-top: 12px;
  padding: 16px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  margin-left: 20px;
}

/* 表单提示文字 */
.form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  line-height: 1.4;
}

/* 数量输入框样式 */
.el-input-number {
  margin-bottom: 0;
}

/* 折叠过渡效果优化 */
.el-collapse-transition-enter-active,
.el-collapse-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.el-collapse-transition-enter-from,
.el-collapse-transition-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 类型与有效期部分样式 */
.type-validity-section {
  background-color: #fafafa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

/* 子标题样式 */
.section-subtitle {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #303133;
  padding-left: 4px;
}

/* 子区域内容样式 */
.section-content {
  margin-bottom: 24px;
}

/* 调整最后一个子区域的底部边距 */
.section-content:last-child {
  margin-bottom: 0;
}

/* 优化下拉选项组样式 */
.el-select-group {
  margin-bottom: 12px;
}

.el-select-group__title {
  font-weight: 700;
  color: #303133;
  background-color: #f5f7fa;
  padding: 12px 16px;
  margin: 0 -16px;
  border-bottom: 1px solid #ebeef5;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 优化分组前缀图标 */
.group-prefix {
  font-size: 18px;
}

/* 调整选项间距 */
.el-select-dropdown__item {
  padding: 8px 16px;
  font-size: 14px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 32px;
}

/* 优化选项悬停效果 */
.el-select-dropdown__item:hover {
  background-color: #ecf5ff !important;
}

/* 优化选项激活效果 */
.el-select-dropdown__item.selected {
  background-color: #ecf5ff !important;
  color: #409eff;
}

/* 调整选项组内边距 */
.el-option-group {
  padding: 8px 0;
}

/* 优化表单提示文字位置 */
.el-form-item {
  position: relative;
}

.form-hint {
  position: absolute;
  bottom: -20px;
  left: 0;
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

/* 调整有效期输入框样式 */
.el-input-number__append {
  background-color: #f5f7fa;
  color: #606266;
}

/* 优化实物商品选择区域的过渡效果 */
.el-col.el-collapse-transition {
  transition: all 0.3s ease;
}
</style>
